import { Immutable } from "immer";
import React from "react";
import { AccordionBlock } from "tsi-common-react/src/components/AccordionBlock/index";

import type { TabsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { DetailBlock } from "../DetailBlock";
import { getSpacingClasses } from "../spacing";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

type Tab = NonNullable<Value["tabs"]>[number];
type TabBody = NonNullable<Tab["tab_body"]>;
type TabBodyItem = TabBody[number];

/**
 * Faithful port of the legacy server-rendered stearns
 * ``cms/blocks/tabs-block.html``. The tab-switching interactivity is owned here
 * via ``activeTab`` state: clicking a tab's link selects it, and the selected
 * index drives ``isCurrent`` on both the matching nav header and its
 * content pane (the first tab is selected by default, matching the
 * legacy ``{% if forloop.first %}`` render).
 */

/**
 * Reproduce the per-child markup the legacy ``tab_body`` StreamBlock emitted.
 * The legacy template did ``{% include_block block.tab_body %}`` on the WHOLE
 * StreamValue, so Wagtail's ``StreamBlock.render_basic`` wrapped each child in
 * ``<div class="w-block-{type} block-{type}">`` (see
 * ``wagtail/blocks/stream_block.py``). thelabui's ``StreamField`` does not emit
 * those wrappers, so render them inline here -- they are load-bearing for the
 * screenshot gate (the tab content styles may target ``.block-*`` children),
 * matching the committed ``DetailSliderBlock`` idiom.
 */
const renderTabBodyItem = (item: Immutable<TabBodyItem>): React.JSX.Element => {
    const className = `w-block-${item.type} block-${item.type}`;
    switch (item.type) {
        case "accordion":
            return (
                <div className={className}>
                    <AccordionBlock value={item.value} />
                </div>
            );
        case "detail":
            return (
                <div className={className}>
                    <DetailBlock value={item.value} />
                </div>
            );
        case "rich_text":
            return (
                <div className={className}>
                    {/* The `rich_text` variant's value is a non-null
                        `RichTextBlock` (string). */}
                    <RichTextBlock value={item.value} />
                </div>
            );
        case "link":
            // The shared LinkBlock is not ported yet; reproduce its markup
            // inline (same idiom as the committed AccordionBlock body). Tracking
            // attrs from the legacy partial are gated on context vars that are
            // not passed to this block, so they are omitted.
            return (
                <div className={className}>
                    <a
                        href={
                            item.value.page?.url ??
                            item.value.external_url ??
                            "#"
                        }
                        className={item.value.is_button ? "button" : undefined}
                    >
                        {item.value.display_name}
                    </a>
                </div>
            );
    }
};

export const TabsBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const tabs = v.tabs ?? [];
    const [activeTab, setActiveTab] = React.useState(0);
    return (
        <section
            className={concatClassNames([
                styles.tabs,
                getSpacingClasses(v.spacing),
            ])}
        >
            {/* NAV headers, inside the tabs bar. Index key: the `tabs` ListBlock
                exposes no stable id, matching the legacy `forloop`-positional
                render. `isCurrent` marks the selected tab. */}
            <div className={styles.tabsBar}>
                {tabs.map((tab, i) => (
                    <header
                        key={i}
                        className={
                            i === activeTab ? styles.isCurrent : undefined
                        }
                    >
                        <a
                            href="#"
                            onClick={(event) => {
                                event.preventDefault();
                                setActiveTab(i);
                            }}
                        >
                            {tab.tab_name}
                        </a>
                    </header>
                ))}
            </div>
            {/* CONTENT panes, direct siblings of the tabs bar.
                Second loop over the same `tabs`, matching the legacy two-loop
                structure; `isCurrent` reveals the selected tab's pane. */}
            {tabs.map((tab, i) => (
                <div
                    key={i}
                    className={concatClassNames([
                        styles.tabContent,
                        i === activeTab ? styles.isCurrent : null,
                    ])}
                >
                    {(tab.tab_body ?? []).map((item) => (
                        // tab_body StreamBlock children carry stable ids.
                        <React.Fragment key={item.id}>
                            {renderTabBodyItem(item)}
                        </React.Fragment>
                    ))}
                </div>
            ))}
        </section>
    );
};
